/**
 * Timezone-aware helpers for turning user-entered wall-clock
 * values into UTC Date objects (and back), using an IANA timezone
 * (e.g. the merchant's "Europe/London").
 *
 * Why this exists: `new Date("2026-05-01T09:00:00")` parses the
 * string in the *browser's* local timezone, not the merchant's.
 * When those differ, the resulting Date represents the wrong instant,
 * and end-time calculations, storage and display all drift by the
 * offset difference.
 */

import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Convert a "YYYY-MM-DD" date + "HH:MM" time entered by a user
 * (meaning: wall-clock time in `timeZone`) into the correct UTC Date.
 *
 * Returns an Invalid Date (matching native `new Date(badString)` semantics)
 * when inputs are missing or malformed, instead of throwing — so callers
 * can use the standard `isNaN(d.getTime())` check.
 *
 * Example: zonedWallTimeToUtc("2026-05-01", "09:00", "Europe/London")
 *   → Date representing 2026-05-01T08:00:00Z (09:00 BST).
 */
export function zonedWallTimeToUtc(
  dateStr: string,
  timeStr: string,
  timeZone: string
): Date {
  if (!dateStr || !timeStr) return new Date(NaN);
  try {
    return fromZonedTime(`${dateStr}T${timeStr}:00`, timeZone);
  } catch {
    return new Date(NaN);
  }
}

/**
 * Return a Date whose local getters (getFullYear, getMonth, getDate,
 * getDay, getHours, ...) report the wall-clock time in `timeZone`.
 *
 * Useful when you need to do calendar arithmetic (setDate, getDay,
 * setHours) in the merchant's timezone rather than the browser's.
 *
 * NOTE: the returned Date's UTC value is NOT a real instant — it's a
 * "zoned view". Do not call .toISOString() on it; use zonedViewToUtc()
 * to convert it back to a true UTC Date.
 */
export function asZoned(date: Date, timeZone: string): Date {
  return toZonedTime(date, timeZone);
}

/**
 * Inverse of asZoned(): given a "zoned view" Date whose local getters
 * report merchant wall-clock time, produce the true UTC Date for that
 * wall-clock moment in `timeZone`.
 *
 * Returns an Invalid Date when `zoned` is invalid or conversion fails,
 * rather than throwing.
 */
export function zonedViewToUtc(zoned: Date, timeZone: string): Date {
  if (isNaN(zoned.getTime())) return new Date(NaN);
  const y = zoned.getFullYear();
  const m = String(zoned.getMonth() + 1).padStart(2, '0');
  const d = String(zoned.getDate()).padStart(2, '0');
  const hh = String(zoned.getHours()).padStart(2, '0');
  const mm = String(zoned.getMinutes()).padStart(2, '0');
  const ss = String(zoned.getSeconds()).padStart(2, '0');
  try {
    return fromZonedTime(`${y}-${m}-${d}T${hh}:${mm}:${ss}`, timeZone);
  } catch {
    return new Date(NaN);
  }
}

/**
 * Format a UTC ISO timestamp as a `<input type="datetime-local">` value
 * ("YYYY-MM-DDTHH:MM") whose displayed wall-clock is in `timeZone`.
 *
 * Returns an empty string for empty/invalid input so it's safe to bind
 * directly to a controlled input.
 */
export function utcIsoToDatetimeLocalValue(iso: string, timeZone: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const zoned = asZoned(d, timeZone);
  const y = zoned.getFullYear();
  const m = String(zoned.getMonth() + 1).padStart(2, '0');
  const day = String(zoned.getDate()).padStart(2, '0');
  const hh = String(zoned.getHours()).padStart(2, '0');
  const mm = String(zoned.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

/**
 * Inverse of utcIsoToDatetimeLocalValue(): given a "YYYY-MM-DDTHH:MM"
 * value from `<input type="datetime-local">`, interpret the wall-clock
 * in `timeZone` and return a true UTC ISO string. Returns '' for
 * empty/malformed input.
 */
export function datetimeLocalValueToUtcIso(value: string, timeZone: string): string {
  if (!value) return '';
  const [dateStr, timeStr] = value.split('T');
  if (!dateStr || !timeStr) return '';
  const hhmm = timeStr.slice(0, 5);
  const d = zonedWallTimeToUtc(dateStr, hhmm, timeZone);
  if (isNaN(d.getTime())) return '';
  return d.toISOString();
}

/**
 * Convert a "YYYY-MM-DD" calendar date picked in `timeZone` to the UTC ISO
 * string for the start of that day (local midnight). DST-aware via
 * `fromZonedTime`, which consults the IANA database for the offset on that
 * specific date.
 *
 * Example: ymdInZoneToUtcStartIso("2025-04-02", "Europe/Berlin")
 *   → "2025-04-01T22:00:00.000Z" (CEST, UTC+2)
 */
export function ymdInZoneToUtcStartIso(ymd: string, timeZone: string): string {
  if (!ymd) return '';
  try {
    return fromZonedTime(`${ymd}T00:00:00`, timeZone).toISOString();
  } catch {
    return '';
  }
}

/**
 * Convert a "YYYY-MM-DD" calendar date picked in `timeZone` to the UTC ISO
 * string for the very end of that day (23:59:59.999 local).
 */
export function ymdInZoneToUtcEndIso(ymd: string, timeZone: string): string {
  if (!ymd) return '';
  try {
    return fromZonedTime(`${ymd}T23:59:59.999`, timeZone).toISOString();
  } catch {
    return '';
  }
}

/**
 * Format a UTC ISO timestamp as a merchant-local ISO 8601 string with an
 * explicit offset, e.g. "2025-04-02T09:00:00+02:00". Suitable for
 * accounting CSV exports — unambiguous yet matches the merchant's
 * wall-clock view of the event.
 */
export function utcIsoToZonedIsoWithOffset(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  try {
    return formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX");
  } catch {
    return '';
  }
}

/**
 * Format a UTC ISO timestamp as the merchant-local "YYYY-MM-DD" calendar
 * date. Useful for export filenames and any date-only display anchored to
 * the merchant's clock.
 */
export function utcIsoToZonedYmd(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  try {
    return formatInTimeZone(new Date(iso), timeZone, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}
